import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StoreService } from '../../services/store.service';
import Languages from '../../models/languages.json';
import { TranslateService } from '@ngx-translate/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, fromEvent, tap } from 'rxjs';
import Categories from '../../models/categories.json';
import Currencies from '../../models/currencies.json';
import { environment } from '../../../environments/environment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { LocationSearchPopupComponent } from '../../modals/location-search-popup/location-search-popup.component';

@Component({
  standalone: false,
  selector: 'app-discover',
  templateUrl: './discover.component.html',
  styleUrl: './discover.component.scss',
})
export class DiscoverComponent implements OnInit, AfterViewInit, OnDestroy {
  noHeaderFooter = false;
  private nestedInBusiness = false;
  businessData: any;
  languages: any = Languages;
  selectedLanguage: any;
  formGroup: FormGroup;
  branches = [];
  search = '';
  @ViewChild('searchInput') searchInput: ElementRef;
  disableButton = false;
  locationLoading = false;
  currentAddress = '';
  teveniUrl = environment.url;
  private lastAddressFetchLatLng: { lat: number; lng: number } | null = null;
  private readonly ADDRESS_REFETCH_METERS_DISTANCE = 30;
  private readonly LAST_ADDRESS_STORAGE_KEY = 'discover-last-address';

  categories = [];
  selectedCategory;
  currencies: any = Currencies;
  bannerResponsiveOptions = [
    { breakpoint: '767px', numVisible: 1, numScroll: 1 },
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private storeService: StoreService,
    private translateService: TranslateService,
    private modalService: BsModalService,
  ) {}

  ngOnInit() {
    this.setupForm();
    this.noHeaderFooter = !!this.route.snapshot.data['noHeaderFooter'];

    const businessName = this.route.snapshot.paramMap.get('business_name');
    this.nestedInBusiness = !!businessName;
    if (businessName) {
      this.storeService
        .ensureCurrentBusiness(businessName)
        .subscribe((response: any) => {
          this.businessData = response;
          this.initializeForBusiness();
        });
    } else {
      this.initializeForBusiness();
    }

    const currentLang = localStorage.getItem('language') || 'en';
    this.selectedLanguage = this.languages.find(
      (lang) => lang.id === currentLang,
    );
  }

  ngAfterViewInit(): void {
    this.getUserLocation(true);
    this.setupListeners();
  }

  ngOnDestroy(): void {
    if (!this.nestedInBusiness) {
      this.storeService.clearTheme();
    }
  }

  private initializeForBusiness(): void {
    this.categories = this.businessData
      ? this.businessData.business_categories || []
      : Categories;
    this.selectedCategory = this.categories[0];
    if (this.businessData) {
      this.storeService.applyTheme(this.businessData.name, this.businessData);
    }
  }

  setLanguage(event: any) {
    this.selectedLanguage = event.value;
    localStorage.setItem('language', event.value.id);
    this.translateService.use(event.value.id);
  }

  setupForm() {
    this.formGroup = new FormGroup({
      latitude: new FormControl('', Validators.required),
      longitude: new FormControl('', Validators.required),
    });
  }

  selectCategory(category: any) {
    if (this.selectedCategory == category) {
      return;
    }
    this.selectedCategory = category;
    this.discoverBusinesses();
  }

  get showBanners(): boolean {
    return (
      this.storeService.isDeliveryBusiness(this.businessData) &&
      this.businessData?.banners?.length > 0
    );
  }

  shouldShowBranchName(branch: any): boolean {
    const businessId = branch.business?.id;
    return (
      this.branches.filter((b: any) => b.business?.id === businessId).length > 1
    );
  }

  getUserLocation(firstTime) {
    this.disableButton = !firstTime;
    this.locationLoading = true;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          this.updateCoordinates(
            position.coords.latitude,
            position.coords.longitude,
          );
          this.discoverBusinesses();
        },
        (error: GeolocationPositionError) => {
          console.log('Geolocation error:', error);
          // Default coordinates if geolocation fails
          this.updateCoordinates(42.6635, 21.1638);
          this.discoverBusinesses();
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    } else {
      console.log('Geolocation is not supported by this browser');
      // Default coordinates if geolocation is not supported
      this.updateCoordinates(42.6635, 21.1638);
      this.discoverBusinesses();
    }
  }

  updateCoordinates(lat: number, lng: number) {
    this.formGroup.get('latitude').setValue(lat);
    this.formGroup.get('longitude').setValue(lng);
    this.reverseGeocodeLocation(lat, lng);
  }

  openLocationSearch() {
    this.modalService.show(LocationSearchPopupComponent, {
      initialState: {
        latitude: this.formGroup.value.latitude,
        longitude: this.formGroup.value.longitude,
        onConfirm: (lat: number, lng: number) => {
          this.updateCoordinates(lat, lng);
          this.discoverBusinesses();
        },
      },
      class: 'alert-modal modal-dialog-centered',
      backdrop: 'static',
    });
  }

  private getDistanceInMeters(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number,
  ): number {
    const R = 6371000; // Earth's radius in meters
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // Persisted in localStorage (not just component memory) because Angular
  // destroys and recreates this component on every navigation to /discover,
  // which would otherwise reset the guard and re-fetch on every visit.
  private hydrateLastAddressFromStorage(): void {
    if (this.lastAddressFetchLatLng) {
      return;
    }
    try {
      const raw = localStorage.getItem(this.LAST_ADDRESS_STORAGE_KEY);
      if (!raw) {
        return;
      }
      const cached = JSON.parse(raw);
      this.lastAddressFetchLatLng = { lat: cached.lat, lng: cached.lng };
      if (!this.currentAddress && cached.address) {
        this.currentAddress = cached.address;
      }
    } catch (error) {
      console.log('Error reading cached location:', error);
    }
  }

  private hasMovedEnoughToRefetchAddress(lat: number, lng: number): boolean {
    if (!this.lastAddressFetchLatLng) {
      return true;
    }
    return (
      this.getDistanceInMeters(
        this.lastAddressFetchLatLng.lat,
        this.lastAddressFetchLatLng.lng,
        lat,
        lng,
      ) > this.ADDRESS_REFETCH_METERS_DISTANCE
    );
  }

  async reverseGeocodeLocation(lat: number, lng: number) {
    this.hydrateLastAddressFromStorage();

    if (!this.hasMovedEnoughToRefetchAddress(lat, lng)) {
      return;
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=16`,
      );
      const data = await response.json();
      const address = this.formatAddress(data);
      if (address) {
        this.currentAddress = address;
      }
      this.lastAddressFetchLatLng = { lat, lng };
      localStorage.setItem(
        this.LAST_ADDRESS_STORAGE_KEY,
        JSON.stringify({ lat, lng, address: this.currentAddress }),
      );
    } catch (error) {
      console.log('Reverse geocode error:', error);
    }
  }

  formatAddress(data: any): string {
    if (!data?.display_name) {
      return '';
    }
    const address = data.display_name.split('/')[0];
    return address.split(',').slice(0, 3).join(',');
  }

  discoverBusinesses() {
    const apiData: any = {};
    const latitude = this.formGroup.value.latitude;
    const longitude = this.formGroup.value.longitude;
    if (latitude && longitude) {
      apiData.latitude = latitude;
      apiData.longitude = longitude;
    }
    if (this.selectedCategory) {
      apiData.category = this.selectedCategory;
    }
    if (this.search) {
      apiData.search = this.search;
    }

    this.locationLoading = true;
    this.storeService
      .discoverBusinesses(apiData, this.businessData?.name)
      .subscribe(
        (response: any) => {
          this.branches = response;
          this.disableButton = false;
          this.locationLoading = false;
        },
        (error: any) => {
          console.log('error:');
          console.log(error);
          this.disableButton = false;
          this.locationLoading = false;
        },
      );
  }

  goToBusiness(branch: any) {
    if (!this.getBranchIsOpen(branch)) {
      return;
    }
    const segments = this.businessData
      ? ['/', this.businessData.name, branch.business.name]
      : ['/', branch.business.name];
    this.router.navigate(segments);
  }

  goToRegister() {
    this.router.navigate(['/register']);
  }

  goToLogin() {
    this.router.navigate(['/login']);
  }

  goToHome() {
    this.router.navigate(['/home']);
  }

  setSearch(event: any) {
    this.search = event.target.value;
  }

  setupListeners() {
    fromEvent(this.searchInput.nativeElement, 'input')
      .pipe(
        tap(() => (this.locationLoading = true)),
        debounceTime(1000),
      )
      .subscribe(() => {
        this.discoverBusinesses();
      });
  }

  getBranchIsOpen(branch) {
    const now = new Date();
    if (
      branch.off_days.includes(
        now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase(),
      )
    ) {
      return false;
    }
    const startHour = new Date();
    startHour.setHours(branch.start_hour.split(':')[0]);
    startHour.setMinutes(branch.start_hour.split(':')[1]);
    const endHour = new Date();
    endHour.setHours(branch.end_hour.split(':')[0]);
    endHour.setMinutes(branch.end_hour.split(':')[1]);

    // Check if this is an overnight operation (end time is next day)
    const isOvernightOperation = endHour <= startHour;

    if (isOvernightOperation) {
      // For overnight operations, business is open if current time is after start time OR before end time
      return now >= startHour || now <= endHour;
    } else {
      // For regular operations, business is open if current time is between start and end time
      return now >= startHour && now <= endHour;
    }
  }

  getBranchEta(branch): string {
    const etaMin = branch.eta_min;
    const etaMax = branch.eta_max;
    return `${etaMin}-${etaMax} min`;
  }

  branchMinOrder(branch): number {
    let minOrder = Number(branch.min_order) || 0;
    if (branch.parentBranch) {
      minOrder = Math.max(minOrder, Number(branch.parentBranch.min_order) || 0);
    }
    return minOrder;
  }

  getBranchMinOrder(branch): string {
    const minOrder = this.branchMinOrder(branch);
    const currency = this.currencies.find(
      (c: any) => c.id === branch.business?.currency,
    );
    const symbol = currency ? currency.symbol : '';
    return `${this.translateService.instant('min')} ${minOrder.toFixed(2)}${symbol}`;
  }

  goToPrivacyPolicy() {
    this.router.navigate(['/privacy-policy']);
  }

  goToTermsOfService() {
    this.router.navigate(['/terms-of-service']);
  }

  goToSocilMedia(app) {
    if (app == 'instagram') {
      window.open(environment.instagramUrl, '_blank', 'noopener,noreferrer');
    }
    if (app == 'facebook') {
      window.open(environment.facebookUrl, '_blank', 'noopener,noreferrer');
    }
    if (app == 'tiktok') {
      window.open(environment.tiktokUrl, '_blank', 'noopener,noreferrer');
    }
  }
}
